/**
 * geomag-wmm.js — the World Magnetic Model (WMM2025) in the browser.
 *
 * WHY THIS EXISTS
 * ---------------
 * A compass needle points at the MAGNETIC pole, a map is drawn to the
 * GEOGRAPHIC one, and the angle between them (declination) reaches 11°+ in
 * European Russia, 15° in the US Pacific Northwest and past 30° in Alaska and
 * northern Canada. Every browser API we can read — iOS `webkitCompassHeading`,
 * Android `deviceorientationabsolute` — reports the MAGNETIC heading. So a page
 * that prints that number under the word "North" is wrong by exactly the local
 * declination, everywhere, forever. Our own /qibla-finder shipped that bug: it
 * compared a TRUE bearing to Mecca against a MAGNETIC device heading.
 *
 * The fix has to be local: an online declination API would make the compass
 * useless in the forest, which is where a compass is used. So the model itself
 * ships to the page — 90 coefficients, ~4 KB, no network, works offline in the
 * PWA cache.
 *
 * WHAT IT IS
 * ----------
 * WMM2025 (epoch 2025.0, valid to 2029.99), the official NOAA/BGS spherical
 * harmonic model to degree 12, released 2024-11-13. Coefficients transcribed
 * verbatim from WMM_2025.COF (g, h, ġ, ḣ in nT and nT/year).
 *
 * VALIDATION — this is the point of the file
 * ------------------------------------------
 * NOAA publishes 108 reference values (WMM2025_TEST_VALUES.txt: 4 epochs ×
 * 27 points, sea level and 100 km, latitudes ±89°). `tests/geomag-wmm.test.mjs`
 * runs THIS file against ALL of them and requires declination within 0.01°.
 * Nothing here is "should be right" — it is checked against the source of truth.
 *
 * API
 * ---
 *   Geomag.field(latDeg, lonDeg, altKm, date) -> {
 *       declination, inclination,     // degrees (D, I)
 *       x, y, z, h, f,                // nT (north, east, down, horizontal, total)
 *       dDdt, dIdt, dFdt,             // per year
 *       decimalYear, inRange          // model validity
 *   }
 *   Geomag.declination(latDeg, lonDeg, date)  -> degrees (east positive)
 *   Geomag.EPOCH / Geomag.VALID_UNTIL / Geomag.MODEL
 */
(function (root, factory) {
    var Geomag = factory();
    if (typeof window !== 'undefined') window.Geomag = Geomag;
    if (typeof module !== 'undefined' && module.exports) module.exports = Geomag;
}(this, function () {
    'use strict';

    var MODEL = 'WMM-2025';
    var EPOCH = 2025.0;
    var VALID_UNTIL = 2030.0;
    var NMAX = 12;

    /**
     * WMM_2025.COF, row order n,m: [g, h, gdot, hdot] in nT and nT/yr.
     * Flat array of 4-tuples indexed by IDX(n,m) so the hot loop does no
     * object lookups. Generated from the official .COF, not retyped by hand.
     */
    var C = [
        1, 0, -29351.8, 0.0, 12.0, 0.0,
        1, 1, -1410.8, 4545.4, 9.7, -21.5,
        2, 0, -2556.6, 0.0, -11.6, 0.0,
        2, 1, 2951.1, -3133.6, -5.2, -27.7,
        2, 2, 1649.3, -815.1, -8.0, -12.1,
        3, 0, 1361.0, 0.0, -1.3, 0.0,
        3, 1, -2404.1, -56.6, -4.2, 4.0,
        3, 2, 1243.8, 237.5, 0.4, -0.3,
        3, 3, 453.6, -549.5, -15.6, -4.1,
        4, 0, 895.0, 0.0, -1.6, 0.0,
        4, 1, 799.5, 278.6, -2.4, -1.1,
        4, 2, 55.7, -133.9, -6.0, 4.1,
        4, 3, -281.1, 212.0, 5.6, 1.6,
        4, 4, 12.1, -375.6, -7.0, -4.4,
        5, 0, -233.2, 0.0, 0.6, 0.0,
        5, 1, 368.9, 45.4, 1.4, -0.5,
        5, 2, 187.2, 220.2, 0.0, 2.2,
        5, 3, -138.7, -122.9, 0.6, 0.4,
        5, 4, -142.0, 43.0, 2.2, 1.7,
        5, 5, 20.9, 106.1, 0.9, 1.9,
        6, 0, 64.4, 0.0, -0.2, 0.0,
        6, 1, 63.8, -18.4, -0.4, 0.3,
        6, 2, 76.9, 16.8, 0.9, -1.6,
        6, 3, -115.7, 48.8, 1.2, -0.4,
        6, 4, -40.9, -59.8, -0.9, 0.9,
        6, 5, 14.9, 10.9, 0.3, 0.7,
        6, 6, -60.7, 72.7, 0.9, 0.9,
        7, 0, 79.5, 0.0, -0.0, 0.0,
        7, 1, -77.0, -48.9, -0.1, 0.6,
        7, 2, -8.8, -14.4, -0.1, 0.5,
        7, 3, 59.3, -1.0, 0.5, -0.8,
        7, 4, 15.8, 23.4, -0.1, 0.0,
        7, 5, 2.5, -7.4, -0.8, -1.0,
        7, 6, -11.1, -25.1, -0.8, 0.6,
        7, 7, 14.2, -2.3, 0.8, -0.2,
        8, 0, 23.2, 0.0, -0.1, 0.0,
        8, 1, 10.8, 7.1, 0.2, -0.2,
        8, 2, -17.5, -12.6, 0.0, 0.5,
        8, 3, 2.0, 11.4, 0.5, -0.4,
        8, 4, -21.7, -9.7, -0.1, 0.4,
        8, 5, 16.9, 12.7, 0.3, -0.5,
        8, 6, 15.0, 0.7, 0.2, -0.6,
        8, 7, -16.8, -5.2, -0.0, 0.3,
        8, 8, 0.9, 3.9, 0.2, 0.2,
        9, 0, 4.6, 0.0, -0.0, 0.0,
        9, 1, 7.8, -24.8, -0.1, -0.3,
        9, 2, 3.0, 12.2, 0.1, 0.3,
        9, 3, -0.2, 8.3, 0.3, -0.3,
        9, 4, -2.5, -3.3, -0.3, 0.3,
        9, 5, -13.1, -5.2, 0.0, 0.2,
        9, 6, 2.4, 7.2, 0.3, -0.1,
        9, 7, 8.6, -0.6, -0.1, -0.2,
        9, 8, -8.7, 0.8, 0.1, 0.4,
        9, 9, -12.9, 10.0, -0.1, 0.1,
        10, 0, -1.3, 0.0, 0.1, 0.0,
        10, 1, -6.4, 3.3, 0.0, 0.0,
        10, 2, 0.2, 0.0, 0.1, -0.0,
        10, 3, 2.0, 2.4, 0.1, -0.2,
        10, 4, -1.0, 5.3, -0.0, 0.1,
        10, 5, -0.6, -9.1, -0.3, -0.1,
        10, 6, -0.9, 0.4, 0.0, 0.1,
        10, 7, 1.5, -4.2, -0.1, 0.0,
        10, 8, 0.9, -3.8, -0.1, -0.1,
        10, 9, -2.7, 0.9, -0.0, 0.2,
        10, 10, -3.9, -9.1, -0.0, -0.0,
        11, 0, 2.9, 0.0, 0.0, 0.0,
        11, 1, -1.5, 0.0, -0.0, -0.0,
        11, 2, -2.5, 2.9, 0.0, 0.1,
        11, 3, 2.4, -0.6, 0.0, -0.0,
        11, 4, -0.6, 0.2, 0.0, 0.1,
        11, 5, -0.1, 0.5, -0.1, -0.0,
        11, 6, -0.6, -0.3, 0.0, -0.0,
        11, 7, -0.1, -1.2, -0.0, 0.1,
        11, 8, 1.1, -1.7, -0.1, -0.0,
        11, 9, -1.0, -2.9, -0.1, 0.0,
        11, 10, -0.2, -1.8, -0.1, 0.0,
        11, 11, 2.6, -2.3, -0.1, 0.0,
        12, 0, -2.0, 0.0, 0.0, 0.0,
        12, 1, -0.2, -1.3, 0.0, -0.0,
        12, 2, 0.3, 0.7, -0.0, 0.0,
        12, 3, 1.2, 1.0, -0.0, -0.1,
        12, 4, -1.3, -1.4, -0.0, 0.1,
        12, 5, 0.6, -0.0, -0.0, -0.0,
        12, 6, 0.6, 0.6, 0.1, -0.0,
        12, 7, 0.5, -0.1, -0.0, -0.0,
        12, 8, -0.1, 0.8, 0.0, 0.0,
        12, 9, -0.4, 0.1, 0.0, -0.0,
        12, 10, -0.2, -1.0, -0.1, -0.0,
        12, 11, -1.3, 0.1, -0.0, 0.0,
        12, 12, -0.7, 0.2, -0.1, -0.1
    ];

    // Unpack into (n,m)-indexed tables once, at load. IDX is triangular.
    var SIZE = (NMAX + 1) * (NMAX + 1);
    var G = new Float64Array(SIZE), H = new Float64Array(SIZE),
        GD = new Float64Array(SIZE), HD = new Float64Array(SIZE);
    function IDX(n, m) { return n * (NMAX + 1) + m; }
    for (var ci = 0; ci < C.length; ci += 6) {
        var i = IDX(C[ci], C[ci + 1]);
        G[i] = C[ci + 2]; H[i] = C[ci + 3]; GD[i] = C[ci + 4]; HD[i] = C[ci + 5];
    }

    // WGS84 + geomagnetic reference radius (km), per the WMM technical report.
    var A = 6378.137, F_FLAT = 1 / 298.257223563;
    var B = A * (1 - F_FLAT);
    var RE = 6371.2;
    var A2 = A * A, B2 = B * B;
    var DEG = Math.PI / 180, RAD = 180 / Math.PI;

    // sqrt table for the Legendre recursion — computed once, not per call.
    var SQ = new Float64Array(2 * NMAX * NMAX + 2 * NMAX + 2);
    for (var s = 0; s < SQ.length; s++) SQ[s] = Math.sqrt(s);

    function decimalYear(date) {
        var d = (date instanceof Date) ? date : (date == null ? new Date() : new Date(date));
        var y = d.getUTCFullYear();
        var start = Date.UTC(y, 0, 1), end = Date.UTC(y + 1, 0, 1);
        return y + (d.getTime() - start) / (end - start);
    }

    /**
     * Schmidt semi-normalized associated Legendre functions P(n,m)(cosθ) and
     * their θ-derivatives, by the standard two-term recursion. Written into
     * caller-supplied arrays so a 60 Hz compass loop allocates nothing.
     */
    function legendre(sinTheta, cosTheta, P, dP) {
        P[IDX(0, 0)] = 1; dP[IDX(0, 0)] = 0;
        for (var n = 1; n <= NMAX; n++) {
            for (var m = 0; m <= n; m++) {
                var i = IDX(n, m);
                if (n === m) {
                    // Sectoral: P(n,n) = sinθ · √((2n−1)/2n) · P(n−1,n−1).
                    // n = 1 is NOT that formula: Schmidt quasi-normalization
                    // carries an extra √2 for m ≥ 1, so the single step from
                    // m=0 to m=1 cancels the √(1/2) and leaves P(1,1) = sinθ.
                    // Getting this wrong scales the whole horizontal field and
                    // showed up as declination off by ~180° near the poles.
                    var k = (n === 1) ? 1 : SQ[2 * n - 1] / SQ[2 * n];
                    var pPrev = P[IDX(n - 1, n - 1)], dPrev = dP[IDX(n - 1, n - 1)];
                    P[i] = k * sinTheta * pPrev;
                    dP[i] = k * (sinTheta * dPrev + cosTheta * pPrev);
                } else {
                    var k1 = (2 * n - 1) / SQ[n * n - m * m];
                    var p1 = P[IDX(n - 1, m)], d1 = dP[IDX(n - 1, m)];
                    var p2 = 0, d2 = 0;
                    if (n - 2 >= m) {
                        var k2 = SQ[(n - 1) * (n - 1) - m * m] / SQ[n * n - m * m];
                        p2 = k2 * P[IDX(n - 2, m)];
                        d2 = k2 * dP[IDX(n - 2, m)];
                    }
                    P[i] = k1 * cosTheta * p1 - p2;
                    dP[i] = k1 * (cosTheta * d1 - sinTheta * p1) - d2;
                }
            }
        }
    }

    var _P = new Float64Array(SIZE), _dP = new Float64Array(SIZE);
    var _cosM = new Float64Array(NMAX + 1), _sinM = new Float64Array(NMAX + 1);

    /**
     * Main evaluation. latDeg/lonDeg are GEODETIC (what GPS gives), altKm is
     * height above the WGS84 ellipsoid, date a Date (defaults to now).
     */
    function field(latDeg, lonDeg, altKm, date) {
        var t = decimalYear(date);
        var dt = t - EPOCH;
        var alt = (typeof altKm === 'number' && isFinite(altKm)) ? altKm : 0;

        // Guard the exact poles: Y' divides by sinθ. 89.9999° is 1 cm of arc —
        // no user is ever there, and a NaN needle would be worse than 0.0001°.
        var lat = Math.max(-89.9999, Math.min(89.9999, latDeg));
        var phi = lat * DEG, lam = lonDeg * DEG;

        // Geodetic → geocentric (spherical) coordinates.
        var sinPhi = Math.sin(phi), cosPhi = Math.cos(phi);
        var rc = A / Math.sqrt(1 - (1 - B2 / A2) * sinPhi * sinPhi);
        var p = (rc + alt) * cosPhi;
        var z = (rc * (B2 / A2) + alt) * sinPhi;
        var r = Math.sqrt(p * p + z * z);
        var phiPrime = Math.atan2(z, p);            // geocentric latitude
        var cosThetaG = Math.sin(phiPrime);          // cos(colatitude) = sin(lat')
        var sinThetaG = Math.cos(phiPrime);

        legendre(sinThetaG, cosThetaG, _P, _dP);

        for (var m = 0; m <= NMAX; m++) {
            _cosM[m] = Math.cos(m * lam);
            _sinM[m] = Math.sin(m * lam);
        }

        var xp = 0, yp = 0, zp = 0;      // geocentric north/east/down
        var xpd = 0, ypd = 0, zpd = 0;   // and their secular rates
        var ratio = RE / r;
        var rPow = ratio * ratio;        // (RE/r)^(n+2) starting at n=0 → ratio^2

        for (var n = 1; n <= NMAX; n++) {
            rPow *= ratio;               // now (RE/r)^(n+2)
            for (var mm = 0; mm <= n; mm++) {
                var i = IDX(n, mm);
                var g = G[i] + dt * GD[i], h = H[i] + dt * HD[i];
                var gd = GD[i], hd = HD[i];
                var cm = _cosM[mm], sm = _sinM[mm];
                var pnm = _P[i], dpnm = _dP[i];

                var gc = g * cm + h * sm;
                var gcd = gd * cm + hd * sm;
                // B = −∇V in spherical coordinates, then geomagnetic axes:
                // X (north) = −B_θ = +(1/r)∂V/∂θ, Y (east) = −(1/(r sinθ))∂V/∂λ,
                // Z (down) = +∂V/∂r. The two sign flips relative to the raw
                // gradient are what put the needle on north instead of south.
                var gs = mm * (g * sm - h * cm);
                var gsd = mm * (gd * sm - hd * cm);

                xp += rPow * gc * dpnm;
                xpd += rPow * gcd * dpnm;
                yp += rPow * gs * pnm / sinThetaG;
                ypd += rPow * gsd * pnm / sinThetaG;
                zp -= rPow * (n + 1) * gc * pnm;
                zpd -= rPow * (n + 1) * gcd * pnm;
            }
        }

        // Rotate the geocentric frame onto the geodetic one.
        var psi = phiPrime - phi;
        var cp = Math.cos(psi), sp = Math.sin(psi);
        var X = xp * cp - zp * sp, Z = xp * sp + zp * cp, Y = yp;
        var Xd = xpd * cp - zpd * sp, Zd = xpd * sp + zpd * cp, Yd = ypd;

        var Hh = Math.sqrt(X * X + Y * Y);
        var Ff = Math.sqrt(Hh * Hh + Z * Z);
        var D = Math.atan2(Y, X) * RAD;
        var I = Math.atan2(Z, Hh) * RAD;

        var Hd = (X * Xd + Y * Yd) / (Hh || 1e-9);
        var Fd = (X * Xd + Y * Yd + Z * Zd) / (Ff || 1e-9);
        var Dd = (X * Yd - Y * Xd) / (Hh * Hh || 1e-9) * RAD;
        var Id = (Hh * Zd - Z * Hd) / (Ff * Ff || 1e-9) * RAD;

        return {
            model: MODEL,
            decimalYear: t,
            inRange: t >= EPOCH && t < VALID_UNTIL,
            declination: D, inclination: I,
            x: X, y: Y, z: Z, h: Hh, f: Ff,
            dDdt: Dd, dIdt: Id, dHdt: Hd, dFdt: Fd,
            dXdt: Xd, dYdt: Yd, dZdt: Zd
        };
    }

    function declination(latDeg, lonDeg, date) {
        return field(latDeg, lonDeg, 0, date).declination;
    }

    return {
        MODEL: MODEL, EPOCH: EPOCH, VALID_UNTIL: VALID_UNTIL, NMAX: NMAX,
        field: field, declination: declination, decimalYear: decimalYear
    };
}));
